/**
 * BubbleCollectionCache - Singleton for caching bubble collection products
 *
 * Solves the Shopify 250-product pagination limit by:
 * 1. Fetching ALL products from a collection via multiple AJAX requests
 * 2. Caching results in memory for reuse across multiple bubble instances
 * 3. Managing concurrent requests via promise-based loading
 *
 * Usage:
 *   const cache = BubbleCollectionCache.getInstance();
 *   const products = await cache.getCollection('all-main-bubble-products');
 */



class BubbleCollectionCache {
  constructor() {
    this.cache = {}; // Layer 1: Collection cache { collectionHandle: { products: [], loading: Promise } }
    this.debug = false; // Set to false to disable console logging
    this.useCustomApi = false; // Disabled: page-based API doesn't work on preview themes
    this.metafieldCache = {}; // Layer 2: Individual metafield cache { handle: metafields }
    this.bubbleGroupCache = {}; // Layer 3: Bubble group cache { cacheKey: { data: [], timestamp, ttl } }
    this.defaultTTL = 300000; // 5 minutes
  }

  static getInstance() {
    if (!window.bubbleCollectionCache) {
      window.bubbleCollectionCache = new BubbleCollectionCache();
    }
    return window.bubbleCollectionCache;
  }

  /**
   * Get all products from a collection (cached)
   * @param {string} collectionHandle - Collection handle (e.g., 'all-main-bubble-products')
   * @returns {Promise<Array>} - Array of product objects
   */
  async getCollection(collectionHandle) {
    // Return cached products if available
    if (this.cache[collectionHandle]?.products) {
      if (this.debug) {
        console.log(`[BubbleCache] ✅ Cache HIT for "${collectionHandle}" (${this.cache[collectionHandle].products.length} products)`);
      }
      return this.cache[collectionHandle].products;
    }

    // If loading is in progress, wait for it
    if (this.cache[collectionHandle]?.loading) {
      if (this.debug) {
        console.log(`[BubbleCache] ⏳ Waiting for in-progress load of "${collectionHandle}"`);
      }
      return await this.cache[collectionHandle].loading;
    }

    // Start new fetch
    if (this.debug) {
      console.log(`[BubbleCache] 🔄 Cache MISS - Fetching "${collectionHandle}"...`);
    }

    const loadingPromise = this._fetchAllProducts(collectionHandle);
    this.cache[collectionHandle] = { loading: loadingPromise };

    try {
      const products = await loadingPromise;
      this.cache[collectionHandle] = { products };

      if (this.debug) {
        console.log(`[BubbleCache] ✅ Cached "${collectionHandle}" (${products.length} products)`);
      }

      return products;
    } catch (error) {
      delete this.cache[collectionHandle]; // Clear failed attempt
      console.error(`[BubbleCache] ❌ Failed to fetch "${collectionHandle}":`, error);
      throw error;
    }
  }

  /**
   * Fetch all products from collection via paginated AJAX requests
   * @private
   */
  async _fetchAllProducts(collectionHandle) {
    // Try custom API first (includes metafields), fallback to Collections API
    if (this.useCustomApi) {
      try {
        if (this.debug) {
          console.log(`[BubbleCache] 🔄 Attempting custom API with metafields for "${collectionHandle}"`);
        }
        return await this._fetchFromCustomApi(collectionHandle);
      } catch (error) {
        console.warn(`[BubbleCache] ⚠️ Custom API failed, falling back to Collections API:`, error);
        // Fall through to Collections API
      }
    }

    // Standard Collections API (no metafields)
    return await this._fetchFromCollectionsApi(collectionHandle);
  }

  /**
   * Fetch from custom Liquid API endpoint (includes metafields)
   * @private
   */
  async _fetchFromCustomApi(collectionHandle) {
    const allProducts = [];
    let page = 1;
    let hasMore = true;
    const startTime = performance.now();

    while (hasMore) {
      const url = `/pages/bubble-api?collection=${collectionHandle}&page=${page}&limit=250`;

      if (this.debug) {
        console.log(`[BubbleCache] 📥 Fetching page ${page} from custom API...`);
      }

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const products = data.products || [];

        if (this.debug) {
          console.log(`[BubbleCache] 📦 Page ${page}: ${products.length} products (with metafields)`);

          // Debug: Log first product structure to see available fields
          if (page === 1 && products.length > 0) {
            console.log('[BubbleCache] 🔍 Sample product structure (Custom API):', {
              id: products[0].id,
              title: products[0].title,
              handle: products[0].handle,
              available: products[0].available,
              metafields: products[0].metafields,
              bubbleImageExists: products[0].metafields?.bubble_image?.exists,
              allKeys: Object.keys(products[0])
            });
          }
        }

        allProducts.push(...products);

        // Check if we've reached the end
        if (products.length < 250) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error) {
        console.error(`[BubbleCache] ❌ Error fetching page ${page} from custom API:`, error);
        throw error;
      }
    }

    const duration = (performance.now() - startTime).toFixed(2);

    if (this.debug) {
      console.log(`[BubbleCache] ✅ Fetched ${allProducts.length} products from custom API in ${duration}ms (${page} requests)`);
    }

    return allProducts;
  }

  /**
   * Fetch from standard Collections API (no metafields)
   * @private
   */
  async _fetchFromCollectionsApi(collectionHandle) {
    const allProducts = [];
    let page = 1;
    let hasMore = true;
    const startTime = performance.now();

    if (this.debug) {
      console.log(`[BubbleCache] 📥 Using Collections API (no metafields)`);
    }

    while (hasMore) {
      const url = `/collections/${collectionHandle}/products.json?limit=250&page=${page}`;

      if (this.debug) {
        console.log(`[BubbleCache] 📥 Fetching page ${page} from ${collectionHandle}...`);
      }

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const products = data.products || [];

        if (this.debug) {
          console.log(`[BubbleCache] 📦 Page ${page}: ${products.length} products`);

          // Debug: Log first product structure to see available fields
          if (page === 1 && products.length > 0) {
            console.log('[BubbleCache] 🔍 Sample product structure:', {
              id: products[0].id,
              title: products[0].title,
              handle: products[0].handle,
              available: products[0].available,
              variants: products[0].variants ? products[0].variants.length : 0,
              firstVariantAvailable: products[0].variants?.[0]?.available,
              tags: products[0].tags,
              featured_image: products[0].featured_image,
              images: products[0].images ? products[0].images.length : 0,
              allKeys: Object.keys(products[0])
            });
          }
        }

        allProducts.push(...products);

        // Check if we've reached the end
        if (products.length < 250) {
          hasMore = false;
        } else {
          page++;
        }
      } catch (error) {
        console.error(`[BubbleCache] ❌ Error fetching page ${page}:`, error);
        throw error;
      }
    }

    const duration = (performance.now() - startTime).toFixed(2);

    if (this.debug) {
      console.log(`[BubbleCache] ✅ Fetched ${allProducts.length} products in ${duration}ms (${page} requests)`);
    }

    return allProducts;
  }

  /**
   * Filter cached products by tag
   * @param {string} collectionHandle - Collection handle
   * @param {string} styleTag - Style tag to filter by (e.g., 'molly-style')
   * @returns {Promise<Array>} - Filtered products
   */
  async getProductsByStyleTag(collectionHandle, styleTag) {
    const allProducts = await this.getCollection(collectionHandle);

    const filtered = allProducts.filter(product => {
      return product.tags && product.tags.includes(styleTag);
    });

    if (this.debug) {
      console.log(`[BubbleCache] 🔍 Filtered by "${styleTag}": ${filtered.length}/${allProducts.length} products`);
    }

    return filtered;
  }

  /**
   * Get product metafields via ?view=bubbles endpoint
   * @param {string} productHandle - Product handle
   * @returns {Promise<Object>} - Product data with metafields
   */
  async getProductMetafields(productHandle) {
    // Return cached metafields if available
    if (this.metafieldCache[productHandle]) {
      if (this.debug) {
        console.log(`[BubbleCache] 💾 Metafield cache HIT for "${productHandle}"`);
      }
      return this.metafieldCache[productHandle];
    }

    if (this.debug) {
      console.log(`[BubbleCache] 🔍 Fetching metafields for "${productHandle}"...`);
    }

    try {
      const url = `/products/${productHandle}?view=bubbles`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Important: Use .text() first, then JSON.parse() for Shopify alternate views
      const text = await response.text();
      const productData = JSON.parse(text);

      // Cache the result
      this.metafieldCache[productHandle] = productData;

      if (this.debug) {
        console.log(`[BubbleCache] ✅ Fetched metafields for "${productHandle}"`, {
          bubbleImage: productData.metafields?.bubble_image?.exists ? 'custom image' : 'none',
          styleOrder: productData.metafields?.style_order
        });
      }

      return productData;
    } catch (error) {
      console.error(`[BubbleCache] ❌ Failed to fetch metafields for "${productHandle}":`, error);
      return null;
    }
  }

  /**
   * Check if cache entry is still valid based on TTL
   * @private
   */
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    if (!cacheEntry.timestamp) return true; // Legacy cache without timestamp

    const ttl = cacheEntry.ttl || this.defaultTTL;
    return (Date.now() - cacheEntry.timestamp) < ttl;
  }

  /**
   * Generate cache key for bubble group
   * @private
   */
  getCacheKey(collectionHandle, styleTag, section = 'main') {
    return `${collectionHandle}:${styleTag}:${section}`;
  }

  /**
   * Get bubble group with metafields and sorting (cached)
   * This caches the FULLY PROCESSED bubble group to avoid redundant work
   *
   * @param {string} collectionHandle - Collection handle
   * @param {string} styleTag - Style tag to filter by
   * @param {string} section - Brand section (main, tailgate, wedding)
   * @param {Function} sortFn - Sort function for style_order
   * @returns {Promise<Array>} - Sorted products with metafields
   */
  async getBubbleGroup(collectionHandle, styleTag, section = 'main', sortFn = null) {
    const cacheKey = this.getCacheKey(collectionHandle, styleTag, section);

    // Check in-memory cache
    if (this.bubbleGroupCache[cacheKey]) {
      const cached = this.bubbleGroupCache[cacheKey];

      if (this.isCacheValid(cached)) {
        if (this.debug) {
          console.log(`[BubbleCache] 🎯 Bubble group cache HIT for "${cacheKey}" (${cached.data.length} bubbles)`);
        }
        return cached.data;
      } else {
        if (this.debug) {
          console.log(`[BubbleCache] ⏰ Cache expired for "${cacheKey}", rebuilding...`);
        }
        delete this.bubbleGroupCache[cacheKey];
      }
    }

    if (this.debug) {
      console.log(`[BubbleCache] 🎯 Bubble group cache MISS for "${cacheKey}" - building...`);
    }

    // Build bubble group from scratch
    const startTime = performance.now();

    // 1. Get filtered products
    const products = await this.getProductsByStyleTag(collectionHandle, styleTag);

    if (products.length === 0) {
      if (this.debug) {
        console.log(`[BubbleCache] ⚠️ No products found for "${styleTag}"`);
      }
      return [];
    }

    // 2. Fetch metafields for all products in parallel (with error handling)
    if (this.debug) {
      console.log(`[BubbleCache] 📦 Fetching metafields for ${products.length} products...`);
    }

    const metafieldPromises = products.map(product =>
      this.getProductMetafields(product.handle)
        .catch(err => {
          console.warn(`[BubbleCache] ⚠️ Metafield fetch failed for ${product.handle}:`, err);
          // Return fallback data
          return {
            handle: product.handle,
            metafields: {
              style_order: 999999,
              bubble_image: null,
              style_group: null
            }
          };
        })
    );

    const metafieldResults = await Promise.all(metafieldPromises);

    // 3. Merge metafield data with products
    const productsWithMetafields = products.map((product, index) => {
      const metafieldData = metafieldResults[index];

      if (metafieldData && metafieldData.metafields) {
        return {
          ...product,
          metafields: {
            ...product.metafields,
            style_order: metafieldData.metafields.style_order,
            bubble_image: metafieldData.metafields.bubble_image,
            style_group: metafieldData.metafields.style_group
          }
        };
      }

      return product;
    });

    // 4. Sort if sort function provided
    let finalProducts = productsWithMetafields;
    if (sortFn) {
      finalProducts = [...productsWithMetafields].sort(sortFn);

      if (this.debug) {
        console.log(`[BubbleCache] 🔄 Sorted bubble group by style_order:`,
          finalProducts.map(p => ({
            handle: p.handle,
            style_order: p.metafields?.style_order || 999999
          }))
        );
      }
    }

    // 5. Cache the result
    const cacheEntry = {
      data: finalProducts,
      timestamp: Date.now(),
      ttl: this.defaultTTL
    };

    this.bubbleGroupCache[cacheKey] = cacheEntry;

    const duration = (performance.now() - startTime).toFixed(2);

    if (this.debug) {
      console.log(`[BubbleCache] ✅ Built and cached bubble group "${cacheKey}" in ${duration}ms (${finalProducts.length} bubbles)`);
    }

    return finalProducts;
  }

  /**
   * Clear bubble group cache (call when products are updated)
   */
  clearBubbleGroupCache(cacheKey = null) {
    if (cacheKey) {
      delete this.bubbleGroupCache[cacheKey];
      if (this.debug) {
        console.log(`[BubbleCache] 🗑️ Cleared bubble group cache for "${cacheKey}"`);
      }
    } else {
      this.bubbleGroupCache = {};
      if (this.debug) {
        console.log('[BubbleCache] 🗑️ Cleared all bubble group caches');
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const stats = {
      collections: {},
      metafields: Object.keys(this.metafieldCache).length,
      bubbleGroups: Object.keys(this.bubbleGroupCache).length,
      bubbleGroupKeys: Object.keys(this.bubbleGroupCache)
    };

    Object.keys(this.cache).forEach(handle => {
      stats.collections[handle] = {
        cached: !!this.cache[handle]?.products,
        loading: !!this.cache[handle]?.loading,
        productCount: this.cache[handle]?.products?.length || 0
      };
    });

    if (this.debug) {
      console.log('[BubbleCache] 📊 Cache stats:', {
        collections: Object.keys(this.cache).length,
        metafields: stats.metafields,
        bubbleGroups: stats.bubbleGroups,
        bubbleGroupKeys: stats.bubbleGroupKeys
      });
    }

    return stats;
  }

  /**
   * Clear cache (useful for development/testing)
   */
  clearCache() {
    if (this.debug) {
      console.log('[BubbleCache] 🗑️ Clearing all caches');
    }
    this.cache = {};
    this.metafieldCache = {};
    this.bubbleGroupCache = {};
  }
}

// Initialize singleton
if (typeof window !== 'undefined') {
  window.BubbleCollectionCache = BubbleCollectionCache;
}
